import React, { useRef, useState, useEffect } from "react";

const TourneyDetailsTabBar = ({ tabs, activeTab, onTabChange }) => {
  const containerRef = useRef(null);
  const [showLeftShadow, setShowLeftShadow] = useState(false);
  const [showRightShadow, setShowRightShadow] = useState(false);

  const checkShadows = () => {
    const el = containerRef.current;
    if (!el) return;

    const { scrollLeft, scrollWidth, clientWidth } = el;

    setShowLeftShadow(scrollLeft > 0);
    setShowRightShadow(scrollLeft + clientWidth < scrollWidth - 1);
  };

  // Helper function to scroll active tab into view
  const scrollActiveTabIntoView = (activeTabKey) => {
    const el = containerRef.current;
    if (!el || !activeTabKey) return;

    // Find the index of the active tab
    const activeIndex = tabs.findIndex((tab) => tab.key === activeTabKey);
    if (activeIndex === -1) return;

    const activeTabElement = el?.children?.[activeIndex];
    if (activeTabElement) {
      activeTabElement.scrollIntoView({
        behavior: "smooth",
        inline: "center",
        block: "nearest",
      });
    }
  };

  const handleTabClick = (tab, index) => {
    onTabChange(tab);

    const el = containerRef.current;
    if (el) {
      const activeTabElement = el?.children?.[index];
      if (activeTabElement) {
        activeTabElement.scrollIntoView({
          behavior: "smooth",
          inline: "center",
          block: "nearest",
        });
      }
    }
  };

  useEffect(() => {
    scrollActiveTabIntoView(activeTab);
  }, [activeTab, tabs]);

  useEffect(() => {
    const el = containerRef.current;

    if (!el) return;

    // Initial check
    checkShadows();

    // Set up event listeners for scroll and resize
    el.addEventListener("scroll", checkShadows);
    window.addEventListener("resize", checkShadows);

    // MutationObserver to re-check when children (tabs) change size
    const observer = new MutationObserver(checkShadows);
    observer.observe(el, { childList: true, subtree: true, attributes: true });

    // Cleanup function
    return () => {
      el.removeEventListener("scroll", checkShadows);
      window.removeEventListener("resize", checkShadows);
      observer.disconnect();
    };
  }, []);

  return (
    <div className="flex items-center gap-2 bg-foreground relative">
      {/* Left Shadow */}
      {showLeftShadow && (
        <div className="pointer-events-none absolute top-0 left-0 z-10 h-full w-8 bg-gradient-to-r from-foreground via-foreground/80 to-transparent" />
      )}

      <div
        ref={containerRef}
        className="flex flex-1 mx-3 items-center flex-nowrap overflow-x-auto scrollbar-hide"
      >
        {tabs.map((tab, idx) => (
          <button
            key={idx}
            onClick={() => handleTabClick(tab, idx)}
            className={`
              flex-shrink-0  relative overflow-hidden flex items-center p-2.5 py-3 text-sm cursor-pointer transition-colors duration-200
              ${
                activeTab === tab.key
                  ? "text-gold-primary border-b-2 border-active-tab-2 font-bold"
                  : "font-bold border-b-2 border-transparent text-secondary"
              }
            `}
          >
            {activeTab === tab.key ? (
              <div className="h-2 bg-linear-to-t w-[40%] left-[30%] rounded-100 from-gold-primary from-30% to-gold-secondary/0 blur-[12px] absolute bottom-0" />
            ) : (
              <div />
            )}
            {tab.lable}
          </button>
        ))}
      </div>

      {/* Right Shadow */}
      {showRightShadow && (
        <div className="pointer-events-none absolute top-0 right-0 h-full w-8 bg-gradient-to-l from-foreground via-foreground/80 to-transparent" />
      )}
    </div>
  );
};

export default TourneyDetailsTabBar;
